import { Controller } from "@hotwired/stimulus";

const SOURCE_SELECTOR =
  'select[name*="photo_source_person_id"], input[type="hidden"][name*="photo_source_person_id"]';

export default class extends Controller {
  static targets = [
    "imageContainer",
    "image",
    "sourceLabel",
    "legend",
    "cropControls",
    "cropButton",
    "fileUploadSection",
  ];
  static values = {
    previewUrlTemplate: String,
    hasOwnPhoto: Boolean,
  };

  connect() {
    this.form = this.element.closest("form");
    if (this.form) {
      this.boundHandleFormEvent = this.handleFormEvent.bind(this);
      this.form.addEventListener("change", this.boundHandleFormEvent);
      this.form.addEventListener("input", this.boundHandleFormEvent);
    }

    this.originalImageSrc = this.hasImageTarget ? this.imageTarget.src : "";
    this.originalImageAlt = this.hasImageTarget ? this.imageTarget.alt : "";
    this.originalButtonText = this.hasCropButtonTarget ? this.cropButtonTarget.textContent : "";
    this.originalButtonImageUrl = this.hasCropButtonTarget
      ? this.cropButtonTarget.dataset.imageUrl || ""
      : "";
    this.originalButtonProfileCrop = this.hasCropButtonTarget
      ? this.cropButtonTarget.dataset.profileCrop || ""
      : "";
    this.originalButtonAvatarCrop = this.hasCropButtonTarget
      ? this.cropButtonTarget.dataset.avatarCrop || ""
      : "";

    this.originalProfileCropX = this.element.dataset.imageCropProfileCropXValue || "";
    this.originalProfileCropY = this.element.dataset.imageCropProfileCropYValue || "";
    this.originalProfileCropWidth = this.element.dataset.imageCropProfileCropWidthValue || "";
  }

  disconnect() {
    if (this.form && this.boundHandleFormEvent) {
      this.form.removeEventListener("change", this.boundHandleFormEvent);
      this.form.removeEventListener("input", this.boundHandleFormEvent);
    }
  }

  handleFormEvent(event) {
    if (!event.target.matches(SOURCE_SELECTOR)) return;

    this.handleSourceChange(event.target.value);
  }

  async handleSourceChange(personId) {
    if (!personId) {
      this.clearSourcePreview();
      return;
    }

    const url = this.previewUrlTemplateValue.replace("__ID__", personId);

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        this.clearSourcePreview();
        return;
      }

      const json = await response.json();
      const data = json.person || json;
      if (data.photo_url) {
        this.showSourcePreview(data);
      } else {
        this.clearSourcePreview();
      }
    } catch (e) {
      console.warn("Failed to fetch photo preview:", e);
      this.clearSourcePreview();
    }
  }

  showSourcePreview(data) {
    if (this.hasImageContainerTarget) {
      this.imageContainerTarget.style.display = "";
    }
    if (this.hasImageTarget) {
      this.imageTarget.src = data.photo_url;
      this.imageTarget.alt = `Photo from ${data.full_name}`;
      this.imageTarget.style.display = "";
    }

    if (this.hasSourceLabelTarget) {
      this.sourceLabelTarget.textContent = `Photo will be copied from ${data.full_name}`;
      this.sourceLabelTarget.style.display = "";
    }

    if (this.hasCropControlsTarget) {
      this.cropControlsTarget.style.display = "";
    }
    if (this.hasCropButtonTarget) {
      this.cropButtonTarget.textContent = "Set crop areas for thumbnails";
      this.cropButtonTarget.dataset.imageUrl = data.photo_url;
      this.cropButtonTarget.dataset.profileCrop = JSON.stringify(data.profile_crop || {});
      this.cropButtonTarget.dataset.avatarCrop = "{}";
    }

    if (data.profile_crop) {
      this.element.dataset.imageCropProfileCropXValue = data.profile_crop.x;
      this.element.dataset.imageCropProfileCropYValue = data.profile_crop.y;
      this.element.dataset.imageCropProfileCropWidthValue = data.profile_crop.width;
    }

    if (this.hasLegendTarget) {
      this.legendTarget.style.display = "none";
    }
    if (this.hasFileUploadSectionTarget) {
      this.fileUploadSectionTarget.style.display = "none";
    }
  }

  clearSourcePreview() {
    if (this.hasOwnPhotoValue) {
      if (this.hasImageContainerTarget) {
        this.imageContainerTarget.style.display = "";
      }
      if (this.hasImageTarget) {
        this.imageTarget.src = this.originalImageSrc;
        this.imageTarget.alt = this.originalImageAlt;
      }
      if (this.hasLegendTarget) {
        this.legendTarget.style.display = "";
      }
      if (this.hasCropControlsTarget) {
        this.cropControlsTarget.style.display = "";
      }
      if (this.hasCropButtonTarget) {
        this.cropButtonTarget.textContent = this.originalButtonText;
        this.cropButtonTarget.dataset.imageUrl = this.originalButtonImageUrl;
        this.cropButtonTarget.dataset.profileCrop = this.originalButtonProfileCrop;
        this.cropButtonTarget.dataset.avatarCrop = this.originalButtonAvatarCrop;
      }
    } else {
      if (this.hasImageContainerTarget) {
        this.imageContainerTarget.style.display = "none";
      }
      if (this.hasCropControlsTarget) {
        this.cropControlsTarget.style.display = "none";
      }
    }

    if (this.hasSourceLabelTarget) {
      this.sourceLabelTarget.style.display = "none";
    }

    if (this.hasFileUploadSectionTarget) {
      this.fileUploadSectionTarget.style.display = "";
    }

    this.element.dataset.imageCropProfileCropXValue = this.originalProfileCropX;
    this.element.dataset.imageCropProfileCropYValue = this.originalProfileCropY;
    this.element.dataset.imageCropProfileCropWidthValue = this.originalProfileCropWidth;
  }
}
